import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/date-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ClientPreviewModal from '../components/ClientPreviewModal';
import NewClientForm from '../components/NewClientForm';
import { Plus, Search, Pencil, Trash2, Zap, Flame, Droplets, Clock, CheckCircle, PauseCircle, ArrowUpDown, ArrowUp, ArrowDown, FileText, Upload, Download, X, FileUp, Recycle, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, UserPlus, Tag, Settings2 , XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  in_corso: { label: 'In Corso', rowBg: 'bg-blue-50 border-l-4 border-l-blue-400', icon: Clock },
  sospeso: { label: 'Sospeso', rowBg: 'bg-yellow-50 border-l-4 border-l-yellow-400', icon: PauseCircle },
  completato: { label: 'Completato', rowBg: 'bg-green-50 border-l-4 border-l-green-400', icon: CheckCircle },
  annullato: { label: 'Annullato', rowBg: 'bg-red-50 border-l-4 border-l-red-500', icon: XCircle },
};

const tipoConfig = {
  luce: { label: 'Luce', icon: Zap, color: 'text-yellow-500' },
  gas: { label: 'Gas', icon: Flame, color: 'text-orange-500' },
  acqua: { label: 'Acqua', icon: Droplets, color: 'text-cyan-500' },
  alia: { label: 'Alia', icon: Recycle, color: 'text-green-600' },
};

// Tipi utenza aggiunti dall'utente (oltre a Luce/Gas/Acqua/Alia): sono etichette
// semplici, senza campi tecnici dedicati, quindi hanno un'icona generica e un
// colore a scelta tra questi (classi scritte per intero per il purge di Tailwind).
const CUSTOM_TYPE_COLOR_TEXT = {
  slate: 'text-slate-500', blue: 'text-blue-500', green: 'text-green-600', yellow: 'text-yellow-500',
  orange: 'text-orange-500', red: 'text-red-500', pink: 'text-pink-500', purple: 'text-purple-500', cyan: 'text-cyan-500',
};
const CUSTOM_TYPE_COLOR_DOT = {
  slate: 'bg-slate-400', blue: 'bg-blue-400', green: 'bg-green-400', yellow: 'bg-yellow-400',
  orange: 'bg-orange-400', red: 'bg-red-400', pink: 'bg-pink-400', purple: 'bg-purple-400', cyan: 'bg-cyan-400',
};
const CUSTOM_TYPE_COLOR_SOLID = {
  slate: 'bg-slate-500', blue: 'bg-blue-500', green: 'bg-green-500', yellow: 'bg-yellow-500',
  orange: 'bg-orange-500', red: 'bg-red-500', pink: 'bg-pink-500', purple: 'bg-purple-500', cyan: 'bg-cyan-500',
};

function FornitoreAutocomplete({ value, onChange, fornitori, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = fornitori.filter(f => f.toLowerCase().includes((filter || value || '').toLowerCase()));

  return (
    <div className="relative" ref={ref}>
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setFilter(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        data-testid={testId}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {filtered.map(f => (
            <div key={f} className="px-3 py-2 text-sm cursor-pointer hover:bg-slate-100" onClick={() => { onChange(f); setOpen(false); }}>
              {f}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClientSearch({ clients, value, onChange, testId, onCreateNew }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedClient = clients.find(c => c.id === value);
  const displayName = selectedClient
    ? (selectedClient.is_company && selectedClient.company_name ? selectedClient.company_name : `${selectedClient.last_name || ''} ${selectedClient.first_name || ''}`.trim())
    : '';

  const filtered = clients.filter(c => {
    if (!search) return true;
    const searchable = [
      c.first_name,
      c.last_name,
      c.company_name,
      c.business_name,
      c.ragione_sociale,
      c.fiscal_code,
      c.codice_fiscale,
      c.piva,
      c.client_code,
    ].filter(Boolean).join(' ').toLowerCase();
    const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
    return tokens.every(t => searchable.includes(t));
  });

  return (
    <div className="relative" ref={ref}>
      <Input
        value={open ? search : displayName}
        onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setSearch(''); }}
        placeholder="Cerca cliente per nome o cognome..."
        data-testid={testId}
      />
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {onCreateNew && (
            <div className="sticky top-0 px-3 py-2 text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 cursor-pointer border-b flex items-center gap-2"
              onClick={() => { onCreateNew(search); setOpen(false); }} data-testid="client-create-new">
              <UserPlus className="w-4 h-4" /> Crea nuovo cliente{search ? `: "${search}"` : ''}
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">Nessun cliente trovato</div>
          ) : (
            <>
              {filtered.slice(0, 50).map(c => {
                const name = c.is_company && c.company_name ? c.company_name : `${c.last_name || ''} ${c.first_name || ''}`.trim();
                return (
                  <div key={c.id} className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 ${c.id === value ? 'bg-slate-50 font-medium' : ''}`}
                    onClick={() => { onChange(c.id); setSearch(''); setOpen(false); }}>
                    {name} {c.fiscal_code ? <span className="text-slate-400 text-xs ml-2">{c.fiscal_code}</span> : null}
                  </div>
                );
              })}
              {filtered.length > 50 && (
                <div className="px-3 py-2 text-xs text-slate-400">…e altri {filtered.length - 50}. Affina la ricerca.</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function Utilities() {
  const { user } = useAuth();
  const [practices, setPractices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewClientId, setPreviewClientId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const toggleGroup = (k) => setExpandedGroups((g) => ({ ...g, [k]: !(g[k] ?? true) }));

  // --- Nuovo cliente rapido dentro la pratica ---
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClientPrefill, setNewClientPrefill] = useState('');
  const openNewClientForm = (prefill) => { setNewClientPrefill(prefill || ''); setNewClientOpen(true); };
  const handleNewClientCreated = async (created) => {
    await fetchClients();
    setFormData(f => ({ ...f, client_id: created.id, asset_id: '' }));
    fetchClientAssets(created.id);
    setNewClientOpen(false);
    toast.success('Cliente collegato alla pratica');
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPractice, setEditingPractice] = useState(null);
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('cerca') || ''); // ?cerca= dal riepilogo cliente
  const [filterStato, setFilterStato] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc'); // ultimo inserimento in cima
  const [currentPage, setCurrentPage] = useState(1);
  // Una pagina = un anno di pratiche (per data di inserimento): pagina 1 = ultimi 12 mesi
  const ANNO_MS = 365 * 24 * 60 * 60 * 1000;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStato, filterTipo]);
  const [fornitori, setFornitori] = useState([]);
  const [utilityTypes, setUtilityTypes] = useState([]);
  const [typesDialogOpen, setTypesDialogOpen] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [newTypeColor, setNewTypeColor] = useState('slate');
  const [savingType, setSavingType] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '', asset_id: '', tipo_utenza: 'luce', descrizione: '', stato: 'in_corso',
    costo_pratica: '', notes: '', fornitore_da: '', fornitore_a: '', modulo: '', data_scadenza: ''
  });
  const [clientAssets, setClientAssets] = useState([]);

  // PDF Template state
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const templateFileRef = useRef(null);

  // PDF Fill state
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [fillPractice, setFillPractice] = useState(null);
  const [fillingPdf, setFillingPdf] = useState(false);
  const uploadFillRef = useRef(null);

  const fetchPractices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/utility-practices`);
      setPractices(res.data);
    } catch (e) { toast.error('Errore nel caricamento pratiche'); }
    finally { setLoading(false); }
  }, []);

  const fetchClients = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/clients`); setClients(res.data); } catch (e) {}
  }, []);

  const fetchFornitori = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/utility-practices/fornitori`); setFornitori(res.data); } catch (e) {}
  }, []);

  const fetchTemplates = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/pdf-templates`); setTemplates(res.data); } catch (e) {}
  }, []);

  const fetchUtilityTypes = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/utility-practices/types`); setUtilityTypes(res.data); } catch (e) {}
  }, []);

  useEffect(() => { fetchPractices(); fetchClients(); fetchFornitori(); fetchTemplates(); fetchUtilityTypes(); }, [fetchPractices, fetchClients, fetchFornitori, fetchTemplates, fetchUtilityTypes]);

  // Helper: config (label/icona/colore) di un tipo utenza, builtin o custom
  const getTipoConfig = (key) => {
    if (tipoConfig[key]) return tipoConfig[key];
    const custom = utilityTypes.find(t => t.key === key && !t.is_builtin);
    if (custom) return { label: custom.label, icon: Tag, color: CUSTOM_TYPE_COLOR_TEXT[custom.color] || 'text-slate-500' };
    return { label: key || '-', icon: Tag, color: 'text-slate-400' };
  };

  const handleAddUtilityType = async () => {
    const label = newTypeLabel.trim();
    if (!label) return;
    setSavingType(true);
    try {
      await axios.post(`${API}/api/utility-practices/types`, { label, color: newTypeColor });
      setNewTypeLabel('');
      setNewTypeColor('slate');
      toast.success('Tipo utenza aggiunto');
      fetchUtilityTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nell\'aggiunta del tipo');
    } finally {
      setSavingType(false);
    }
  };

  const handleDeleteUtilityType = async (key) => {
    if (!window.confirm('Eliminare questo tipo utenza?')) return;
    try {
      await axios.delete(`${API}/api/utility-practices/types/${key}`);
      toast.success('Tipo utenza eliminato');
      fetchUtilityTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nell\'eliminazione del tipo');
    }
  };

  // Refresh clienti quando l'utente torna sulla tab
  useEffect(() => {
    const onFocus = () => fetchClients();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchClients]);

  const fetchClientAssets = async (clientId) => {
    try {
      const res = await axios.get(`${API}/api/assets?client_id=${clientId}&asset_type=utenze`);
      setClientAssets(res.data);
    } catch (e) { setClientAssets([]); }
  };

  const openNewDialog = () => {
    setEditingPractice(null);
    setNewClientOpen(false);
    setFormData({ client_id: '', asset_id: '', tipo_utenza: 'luce', descrizione: '', stato: 'in_corso', costo_pratica: '', notes: '', fornitore_da: '', fornitore_a: '', modulo: '', data_scadenza: '' });
    setClientAssets([]);
    setDialogOpen(true);
  };

  const openEditDialog = (practice) => {
    setEditingPractice(practice);
    setNewClientOpen(false);
    setFormData({
      client_id: practice.client_id || '', asset_id: practice.asset_id || '',
      tipo_utenza: practice.tipo_utenza || 'luce', descrizione: practice.descrizione || '',
      stato: practice.stato || 'in_corso', costo_pratica: practice.costo_pratica || '',
      notes: practice.notes || '', fornitore_da: practice.fornitore_da || '',
      fornitore_a: practice.fornitore_a || '', modulo: practice.modulo || '',
      data_scadenza: practice.data_scadenza || ''
    });
    if (practice.client_id) fetchClientAssets(practice.client_id);
    setDialogOpen(true);
  };

  // keepOpen: usato dal pulsante "Aggiungi" per inserire più pratiche di
  // seguito sullo stesso cliente senza chiudere il dialog. "Crea Pratica"
  // (keepOpen=false) salva e chiude, per il caso di una sola pratica.
  const handleSave = async (keepOpen = false) => {
    if (!formData.client_id) { toast.error('Seleziona un cliente'); return; }
    const payload = { ...formData, costo_pratica: parseFloat(formData.costo_pratica) || 0 };
    try {
      if (editingPractice) {
        await axios.put(`${API}/api/utility-practices/${editingPractice.id}`, payload);
        toast.success('Pratica aggiornata');
        setDialogOpen(false);
      } else {
        await axios.post(`${API}/api/utility-practices`, payload);
        if (keepOpen) {
          toast.success("Pratica aggiunta, inseriscine un'altra");
          // Mantiene cliente e utenza selezionati, svuota solo i campi della pratica
          setFormData(f => ({
            ...f,
            descrizione: '',
            stato: 'in_corso',
            data_scadenza: '',
            fornitore_da: '',
            fornitore_a: '',
            costo_pratica: '',
            notes: '',
          }));
        } else {
          toast.success('Pratica creata');
          setDialogOpen(false);
        }
      }
      fetchPractices();
      fetchFornitori();
    } catch (e) { toast.error('Errore nel salvataggio'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa pratica?')) return;
    try {
      await axios.delete(`${API}/api/utility-practices/${id}`);
      toast.success('Pratica eliminata');
      fetchPractices();
    } catch (e) { toast.error('Errore eliminazione'); }
  };

  const handleStatusChange = async (practice, newStatus) => {
    try {
      await axios.put(`${API}/api/utility-practices/${practice.id}`, { stato: newStatus });
      toast.success(`Stato aggiornato: ${statusConfig[newStatus]?.label}`);
      fetchPractices();
    } catch (e) { toast.error('Errore aggiornamento stato'); }
  };

  // ========== PDF Template Functions ==========
  const handleTemplateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo file PDF sono accettati');
      return;
    }
    setUploadingTemplate(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', templateName || file.name.replace(/\.pdf$/i, ''));
      await axios.post(`${API}/api/pdf-templates/upload`, fd);
      toast.success('Template PDF caricato');
      setTemplateName('');
      fetchTemplates();
      if (templateFileRef.current) templateFileRef.current.value = '';
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore nel caricamento';
      toast.error(msg);
    } finally { setUploadingTemplate(false); }
  };

  const handleTemplateDelete = async (id) => {
    if (!window.confirm('Eliminare questo template?')) return;
    try {
      await axios.delete(`${API}/api/pdf-templates/${id}`);
      toast.success('Template eliminato');
      fetchTemplates();
    } catch (e) { toast.error('Errore eliminazione template'); }
  };

  // ========== PDF Fill Functions ==========
  const openFillDialog = (practice) => {
    setFillPractice(practice);
    setFillDialogOpen(true);
  };

  const handleFillFromTemplate = async (templateId) => {
    if (!fillPractice) return;
    setFillingPdf(true);
    try {
      const res = await axios.post(
        `${API}/api/utility-practices/${fillPractice.id}/fill-pdf/${templateId}`,
        {},
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'modulo_compilato.pdf';
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF compilato e scaricato');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore nella compilazione del PDF';
      toast.error(msg);
    } finally { setFillingPdf(false); }
  };

  const handleFillFromUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !fillPractice) return;
    setFillingPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/api/utility-practices/${fillPractice.id}/fill-pdf-upload`,
        fd,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'modulo_compilato.pdf';
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF compilato e scaricato');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore nella compilazione del PDF';
      toast.error(msg);
    } finally {
      setFillingPdf(false);
      if (uploadFillRef.current) uploadFillRef.current.value = '';
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-300" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-slate-700" /> : <ArrowDown className="w-3 h-3 ml-1 text-slate-700" />;
  };

  const statusOrder = { in_corso: 1, sospeso: 2, completato: 3, annullato: 4 };

  const filtered = practices
    .filter(p => {
      if (filterStato !== 'all' && p.stato !== filterStato) return false;
      if (filterTipo !== 'all' && p.tipo_utenza !== filterTipo) return false;
      if (searchTerm) {
        const norm = (x) => (x || '').replace(/\s+/g, ' ').trim().toLowerCase();  // nomi salvati con doppi spazi
        const s = norm(searchTerm);
        return norm(p.client_name).includes(s) || norm(p.descrizione).includes(s);
      }
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'data_scadenza') {
        const da = a.data_scadenza ? new Date(a.data_scadenza) : new Date('9999-12-31');
        const db2 = b.data_scadenza ? new Date(b.data_scadenza) : new Date('9999-12-31');
        cmp = da - db2;
      } else if (sortField === 'created_at') {
        cmp = new Date(a.created_at || 0) - new Date(b.created_at || 0);
      } else if (sortField === 'stato') {
        cmp = (statusOrder[a.stato] || 0) - (statusOrder[b.stato] || 0);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

  // Pagina per anno; con ricerca o filtri attivi si vedono tutti i risultati, di ogni anno
  const ricercaAttiva = !!searchTerm || filterStato !== 'all' || filterTipo !== 'all';
  const adesso = Date.now();
  const piuVecchia = filtered.reduce((min, p) => Math.min(min, new Date(p.created_at || adesso).getTime()), adesso);
  const totalPages = ricercaAttiva ? 1 : Math.max(1, Math.ceil((adesso - piuVecchia + 1) / ANNO_MS));
  const fineAnno = adesso - (currentPage - 1) * ANNO_MS;
  const inizioAnno = fineAnno - ANNO_MS;
  const filteredAnno = ricercaAttiva ? filtered : filtered.filter((p) => {
    const t = new Date(p.created_at || adesso).getTime();
    return t > inizioAnno && t <= fineAnno;
  });
  const formatGiorno = (ms) => new Date(ms).toLocaleDateString('it-IT');

  // Raggruppa le pratiche per cliente (ordine = quello di 'filtered')
  const groups = (() => {
    const m = new Map();
    for (const p of filteredAnno) {
      const k = p.client_id || `name:${p.client_name || '—'}`;
      if (!m.has(k)) m.set(k, { key: k, client_id: p.client_id || null, client_name: p.client_name || '-', items: [] });
      m.get(k).items.push(p);
    }
    return Array.from(m.values());
  })();

  const paginatedGroups = groups;

  const allGroupsOpen = groups.every(g => expandedGroups[g.key] ?? true);
  const toggleAllGroups = () => {
    const next = {};
    groups.forEach(g => { next[g.key] = !allGroupsOpen; });
    setExpandedGroups(next);
  };

  const getAssetLabel = (asset) => {
    const tipo = { luce: 'Luce', gas: 'Gas', acqua: 'Acqua' }[asset.tipo_utenza] || '';
    return `${tipo} - ${asset.pod_pdr_codice || asset.fornitore || 'Utenza'}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Utenze</h1>
          <p className="text-slate-600 mt-1">Gestione pratiche Luce, Gas, Acqua</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setTemplateDialogOpen(true)} data-testid="manage-templates-btn">
            <FileText className="w-4 h-4 mr-2" />Moduli PDF
          </Button>
          <Button onClick={openNewDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="new-practice-btn">
            <Plus className="w-4 h-4 mr-2" />Nuova Pratica
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Cerca per cliente o descrizione..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-practices" />
        </div>
        <Select value={filterStato} onValueChange={setFilterStato}>
          <SelectTrigger className="w-[160px]" data-testid="filter-stato"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="in_corso">In Corso</SelectItem>
            <SelectItem value="sospeso">Sospeso</SelectItem>
            <SelectItem value="completato">Completato</SelectItem>
            <SelectItem value="annullato">Annullato</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger className="w-[140px]" data-testid="filter-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {utilityTypes.map(t => (<SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats: clic su un riquadro = mostra solo quelle pratiche (di nuovo clic = tutte) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          ['all', practices.length, 'Totale Pratiche', 'bg-white border-slate-200', 'text-slate-900', 'text-slate-500', 'ring-slate-900'],
          ['in_corso', practices.filter(p => p.stato === 'in_corso').length, 'In Corso', 'bg-blue-50 border-blue-200', 'text-blue-700', 'text-blue-600', 'ring-blue-500'],
          ['sospeso', practices.filter(p => p.stato === 'sospeso').length, 'Sospese', 'bg-yellow-50 border-yellow-200', 'text-yellow-700', 'text-yellow-600', 'ring-yellow-500'],
          ['completato', practices.filter(p => p.stato === 'completato').length, 'Completate', 'bg-green-50 border-green-200', 'text-green-700', 'text-green-600', 'ring-green-500'],
          ['annullato', practices.filter(p => p.stato === 'annullato').length, 'Annullate', 'bg-red-50 border-red-200', 'text-red-700', 'text-red-600', 'ring-red-500'],
        ].map(([stato, n, etichetta, sfondo, numero, testo, anello]) => (
          <button key={stato} type="button" onClick={() => setFilterStato(filterStato === stato ? 'all' : stato)}
            className={`text-left rounded-xl border p-4 transition hover:shadow-md cursor-pointer ${sfondo} ${filterStato === stato ? `ring-2 ${anello}` : ''}`}
            data-testid={`stat-${stato}`}>
            <div className={`text-2xl font-bold ${numero}`}>{n}</div>
            <div className={`text-sm ${testo}`}>{etichetta}</div>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50/60">
          <span className="text-xs text-slate-500">{groups.length} client{groups.length === 1 ? 'e' : 'i'} · {filtered.length} pratiche</span>
          <button
            type="button"
            onClick={toggleAllGroups}
            className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800"
            data-testid="utenze-toggle-all"
          >
            {allGroupsOpen ? <ChevronsDownUp className="w-3.5 h-3.5" /> : <ChevronsUpDown className="w-3.5 h-3.5" />}
            {allGroupsOpen ? 'Comprimi tutto' : 'Espandi tutto'}
          </button>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-900 bg-slate-50">
              <th className="w-8 p-3" />
              <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase cursor-pointer select-none" onClick={() => toggleSort('created_at')} data-testid="sort-inserimento">
                <span className="flex items-center">Inserimento <SortIcon field="created_at" /></span>
              </th>
              <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Cliente / Tipo</th>
              <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Cambio Fornitore</th>
              <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Descrizione</th>
              <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Costo</th>
              <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase cursor-pointer select-none" onClick={() => toggleSort('data_scadenza')} data-testid="sort-scadenza">
                <span className="flex items-center">Scadenza <SortIcon field="data_scadenza" /></span>
              </th>
              <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Note</th>
              <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase cursor-pointer select-none" onClick={() => toggleSort('stato')} data-testid="sort-stato">
                <span className="flex items-center">Stato <SortIcon field="stato" /></span>
              </th>
              <th className="text-right p-3 text-xs font-medium text-slate-500 uppercase">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="10" className="text-center py-8 text-slate-500">Caricamento...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="10" className="text-center py-8 text-slate-500">Nessuna pratica trovata</td></tr>
            ) : paginatedGroups.map(group => {
              const gopen = expandedGroups[group.key] ?? true;
              const n = group.items.length;
              const gcounts = group.items.reduce((acc, p) => { acc[p.stato] = (acc[p.stato] || 0) + 1; return acc; }, {});
              const gsummary = ['in_corso', 'sospeso', 'completato', 'annullato']
                .filter(s => gcounts[s]).map(s => `${gcounts[s]} ${statusConfig[s].label.toLowerCase()}`).join(' · ');
              return (
                <React.Fragment key={group.key}>
                  <tr className="border-t-2 border-b border-slate-900 bg-slate-50/80 hover:bg-slate-100 cursor-pointer" onClick={() => toggleGroup(group.key)} data-testid="utenza-group-row">
                    <td className="p-3 text-center w-8">
                      {gopen ? <ChevronDown className="w-4 h-4 text-slate-400 inline" /> : <ChevronRight className="w-4 h-4 text-slate-400 inline" />}
                    </td>
                    <td className="p-3" colSpan={9}>
                      <div className="flex items-center gap-3 flex-wrap">
                        {group.client_id ? (
                          <button type="button" onClick={(e) => { e.stopPropagation(); setPreviewClientId(group.client_id); }}
                            className="text-sm font-semibold text-blue-700 hover:underline" data-testid="practice-client-name">
                            {group.client_name}
                          </button>
                        ) : (
                          <span className="text-sm font-semibold text-slate-900">{group.client_name}</span>
                        )}
                        <Badge className="bg-slate-200 text-slate-700 text-xs">{n} {n === 1 ? 'utenza' : 'utenze'}</Badge>
                        {gsummary && <span className="text-xs text-slate-400">{gsummary}</span>}
                      </div>
                    </td>
                  </tr>
                  {gopen && group.items.map(practice => {
              const tipo = getTipoConfig(practice.tipo_utenza);
              const TipoIcon = tipo.icon;
              const status = statusConfig[practice.stato] || statusConfig.in_corso;
              const isScaduta = practice.data_scadenza && new Date(practice.data_scadenza) < new Date(new Date().toDateString());
              return (
                <tr key={practice.id} className={`border-b border-slate-900 transition-colors ${status.rowBg}`} data-testid="practice-row">
                  <td className="p-3 w-8" />
                  <td className="p-3">
                    <span className="text-sm text-slate-500">{practice.created_at ? new Date(practice.created_at).toLocaleDateString('it-IT') : '-'}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 pl-4">
                      <TipoIcon className={`w-4 h-4 ${tipo.color} flex-shrink-0`} />
                      <span className="text-sm font-medium text-slate-700">{tipo.label}</span>
                    </div>
                  </td>
                  <td className="p-3">
                    {(practice.fornitore_da || practice.fornitore_a) ? (
                      <span className="text-sm text-slate-700">{practice.fornitore_da || '?'} &rarr; {practice.fornitore_a || '?'}</span>
                    ) : <span className="text-sm text-slate-400">-</span>}
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-slate-700">{practice.descrizione || '-'}</span>
                  </td>
                  <td className="p-3">
                    {practice.costo_pratica > 0 ? (
                      <span className="text-sm font-medium text-slate-900"><span className="text-xs mr-0.5">&euro;</span>{Number(practice.costo_pratica).toFixed(2)}</span>
                    ) : <span className="text-sm text-slate-400">-</span>}
                  </td>
                  <td className="p-3">
                    {practice.data_scadenza ? (
                      <span className={`text-sm font-medium ${isScaduta ? 'text-red-600' : 'text-slate-700'}`} data-testid="scadenza-date">
                        {new Date(practice.data_scadenza).toLocaleDateString('it-IT')}
                      </span>
                    ) : <span className="text-sm text-slate-400">-</span>}
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-slate-600 line-clamp-2">{practice.notes || '-'}</span>
                  </td>
                  <td className="p-3">
                    <Select value={practice.stato} onValueChange={(v) => handleStatusChange(practice, v)}>
                      <SelectTrigger className="h-7 w-[130px] border-0 p-0 bg-transparent" data-testid="practice-status-select">
                        <Badge className={`cursor-pointer ${practice.stato === 'completato' ? 'bg-green-200 text-green-800' : practice.stato === 'sospeso' ? 'bg-yellow-200 text-yellow-800' : practice.stato === 'annullato' ? 'bg-red-200 text-red-800' : 'bg-blue-200 text-blue-800'}`}>{status.label}</Badge>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_corso">In Corso</SelectItem>
                        <SelectItem value="sospeso">Sospeso</SelectItem>
                        <SelectItem value="completato">Completato</SelectItem>
            <SelectItem value="annullato">Annullato</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => openFillDialog(practice)} title="Compila PDF" data-testid="fill-pdf-btn">
                        <FileUp className="w-4 h-4 text-indigo-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEditDialog(practice)} data-testid="edit-practice-btn">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(practice.id)} data-testid="delete-practice-btn">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t bg-slate-50/60">
            <span className="text-xs text-slate-500">
              {currentPage === 1 ? 'Ultimi 12 mesi' : `Dal ${formatGiorno(inizioAnno + 86400000)} al ${formatGiorno(fineAnno)}`}
              {' '}— {filteredAnno.length} pratiche, {groups.length} clienti
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <ChevronRight className="w-4 h-4 rotate-180" />
              </Button>
              <span className="text-xs text-slate-600">Pagina {currentPage} di {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Practice Edit/Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPractice ? 'Modifica Pratica' : 'Nuova Pratica Utenza'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <ClientSearch
                clients={clients}
                value={formData.client_id}
                onChange={(id) => { setFormData({ ...formData, client_id: id, asset_id: '' }); fetchClientAssets(id); }}
                onCreateNew={openNewClientForm}
                testId="practice-client-search"
              />

              {newClientOpen && (
                <div className="mt-2">
                  <NewClientForm
                    prefillName={newClientPrefill}
                    onCancel={() => setNewClientOpen(false)}
                    onCreated={handleNewClientCreated}
                  />
                </div>
              )}
            </div>

            {clientAssets.length > 0 && (
              <div className="space-y-2">
                <Label>Bene Utenza Collegato</Label>
                <Select value={formData.asset_id || 'none'} onValueChange={(v) => {
                  const realVal = v === 'none' ? '' : v;
                  const asset = clientAssets.find(a => a.id === realVal);
                  setFormData({ ...formData, asset_id: realVal, tipo_utenza: asset?.tipo_utenza || formData.tipo_utenza });
                }}>
                  <SelectTrigger><SelectValue placeholder="Seleziona utenza..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno</SelectItem>
                    {clientAssets.map(a => (
                      <SelectItem key={a.id} value={a.id}>{getAssetLabel(a)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Tipo Utenza</Label>
                  <button
                    type="button"
                    onClick={() => setTypesDialogOpen(true)}
                    className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                    data-testid="manage-utility-types-btn"
                  ><Settings2 className="w-3 h-3" />Gestisci</button>
                </div>
                <Select value={formData.tipo_utenza} onValueChange={(v) => setFormData({ ...formData, tipo_utenza: v })}>
                  <SelectTrigger data-testid="practice-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {utilityTypes.map(t => (<SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Stato</Label>
                <Select value={formData.stato} onValueChange={(v) => setFormData({ ...formData, stato: v })}>
                  <SelectTrigger data-testid="practice-stato"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_corso">In Corso</SelectItem>
                    <SelectItem value="sospeso">Sospeso</SelectItem>
                    <SelectItem value="completato">Completato</SelectItem>
            <SelectItem value="annullato">Annullato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Data Scadenza</Label>
              <DateInput
                value={formData.data_scadenza}
                onChange={(val) => setFormData({ ...formData, data_scadenza: val })}
                placeholder="gg/mm/aaaa"
                data-testid="practice-data-scadenza"
              />
            </div>

            <div className="space-y-2">
              <Label>Cambio Fornitore</Label>
              <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                <FornitoreAutocomplete
                  value={formData.fornitore_da}
                  onChange={(v) => setFormData({ ...formData, fornitore_da: v })}
                  fornitori={fornitori}
                  placeholder="Da..."
                  testId="practice-fornitore-da"
                />
                <span className="text-sm font-bold text-slate-500 px-1">&rarr;</span>
                <FornitoreAutocomplete
                  value={formData.fornitore_a}
                  onChange={(v) => setFormData({ ...formData, fornitore_a: v })}
                  fornitori={fornitori}
                  placeholder="A..."
                  testId="practice-fornitore-a"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input
                value={formData.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e.target.value.toUpperCase() })}
                placeholder="es. Cambio fornitore luce, Voltura gas..."
                data-testid="practice-descrizione"
              />
            </div>

            <div className="space-y-2">
              <Label>Costo Pratica</Label>
              <Input
                type="number" step="0.01"
                value={formData.costo_pratica}
                onChange={(e) => setFormData({ ...formData, costo_pratica: e.target.value })}
                placeholder="0.00"
                data-testid="practice-costo"
              />
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} placeholder="Note aggiuntive..." />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              {!editingPractice && (
                <Button
                  variant="outline"
                  onClick={() => handleSave(true)}
                  data-testid="add-another-practice-btn"
                  title="Salva questa pratica e inseriscine subito un'altra per lo stesso cliente"
                >
                  Aggiungi
                </Button>
              )}
              <Button onClick={() => handleSave(false)} className="bg-slate-900 hover:bg-slate-800" data-testid="save-practice-btn">
                {editingPractice ? 'Salva' : 'Crea Pratica'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Template Management Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gestione Moduli PDF Editabili</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Carica moduli PDF editabili (con campi compilabili). I dati del cliente verranno inseriti automaticamente nei campi del modulo.
            </p>

            {/* Upload new template */}
            <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
              <Label className="font-medium">Carica Nuovo Modulo</Label>
              <Input
                placeholder="Nome del modulo (opzionale)"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                data-testid="template-name-input"
              />
              <div className="flex gap-2">
                <input
                  type="file"
                  accept=".pdf"
                  ref={templateFileRef}
                  onChange={handleTemplateUpload}
                  className="hidden"
                  data-testid="template-file-input"
                />
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => templateFileRef.current?.click()}
                  disabled={uploadingTemplate}
                  data-testid="upload-template-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingTemplate ? 'Caricamento...' : 'Seleziona PDF Editabile'}
                </Button>
              </div>
            </div>

            {/* Template list */}
            <div className="space-y-2">
              <Label className="font-medium">Moduli Salvati ({templates.length})</Label>
              {templates.length === 0 ? (
                <div className="text-center py-6 text-slate-400 text-sm border rounded-lg">
                  Nessun modulo caricato. Carica un PDF editabile per iniziare.
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map(t => (
                    <div key={t.id} className="flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-slate-50 transition-colors" data-testid="template-item">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{t.name}</div>
                          <div className="text-xs text-slate-400">{t.field_count} campi compilabili</div>
                        </div>
                      </div>
                      <Button
                        size="sm" variant="ghost"
                        className="text-red-500 hover:text-red-700 flex-shrink-0"
                        onClick={() => handleTemplateDelete(t.id)}
                        data-testid="delete-template-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Fill Dialog */}
      <Dialog open={fillDialogOpen} onOpenChange={setFillDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Compila Modulo PDF</DialogTitle>
          </DialogHeader>
          {fillPractice && (
            <div className="space-y-4">
              <div className="bg-slate-50 border rounded-lg p-3">
                <div className="text-sm">
                  <span className="text-slate-500">Cliente:</span>{' '}
                  <span className="font-medium text-slate-900">{fillPractice.client_name}</span>
                </div>
                <div className="text-sm mt-1">
                  <span className="text-slate-500">Pratica:</span>{' '}
                  <span className="text-slate-700">{fillPractice.descrizione || fillPractice.modulo || getTipoConfig(fillPractice.tipo_utenza).label || '-'}</span>
                </div>
              </div>

              {/* Option 1: Use saved template */}
              {templates.length > 0 && (
                <div className="space-y-2">
                  <Label className="font-medium">Usa Modulo Salvato</Label>
                  <div className="space-y-2">
                    {templates.map(t => (
                      <div key={t.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-indigo-50 hover:border-indigo-200 transition-colors cursor-pointer group" data-testid="fill-template-option">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{t.name}</div>
                            <div className="text-xs text-slate-400">{t.field_count} campi</div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          onClick={() => handleFillFromTemplate(t.id)}
                          disabled={fillingPdf}
                          data-testid="fill-from-template-btn"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {fillingPdf ? 'Compilazione...' : 'Compila'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Divider */}
              {templates.length > 0 && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200"></div>
                  <span className="text-xs text-slate-400 uppercase">oppure</span>
                  <div className="flex-1 h-px bg-slate-200"></div>
                </div>
              )}

              {/* Option 2: Upload one-time PDF */}
              <div className="space-y-2">
                <Label className="font-medium">Carica PDF da Compilare</Label>
                <p className="text-xs text-slate-400">Carica un PDF editabile e verrà compilato automaticamente con i dati del cliente</p>
                <input
                  type="file"
                  accept=".pdf"
                  ref={uploadFillRef}
                  onChange={handleFillFromUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => uploadFillRef.current?.click()}
                  disabled={fillingPdf}
                  data-testid="fill-from-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {fillingPdf ? 'Compilazione in corso...' : 'Seleziona PDF e Compila'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Gestione Tipi Utenza: Luce/Gas/Acqua/Alia sono predefiniti (non eliminabili,
          collegati ai campi tecnici dei Beni); i tipi aggiunti qui sono etichette
          semplici usate solo dalle pratiche. */}
      <Dialog open={typesDialogOpen} onOpenChange={setTypesDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Tipi Utenza</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {utilityTypes.map(t => (
                <div key={t.key} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50" data-testid={`utility-type-row-${t.key}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${CUSTOM_TYPE_COLOR_DOT[t.color] || 'bg-slate-400'}`} />
                    <span className="text-sm text-slate-700">{t.label}</span>
                    {t.is_builtin && <span className="text-[10px] text-slate-400">predefinito</span>}
                  </div>
                  {!t.is_builtin && (
                    <button
                      type="button"
                      onClick={() => handleDeleteUtilityType(t.key)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Elimina tipo"
                      data-testid={`delete-utility-type-${t.key}`}
                    ><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t pt-3 space-y-2">
              <Label className="text-sm">Nuovo tipo</Label>
              <Input
                value={newTypeLabel}
                onChange={(e) => setNewTypeLabel(e.target.value)}
                placeholder="es. Telefonia"
                data-testid="new-utility-type-label"
              />
              <div className="flex gap-1.5 flex-wrap">
                {Object.keys(CUSTOM_TYPE_COLOR_SOLID).map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewTypeColor(c)}
                    className={`w-6 h-6 rounded-full ${CUSTOM_TYPE_COLOR_SOLID[c]} ${newTypeColor === c ? 'ring-2 ring-offset-1 ring-slate-900' : ''}`}
                    title={c}
                    data-testid={`new-utility-type-color-${c}`}
                  />
                ))}
              </div>
              <Button
                onClick={handleAddUtilityType}
                disabled={savingType || !newTypeLabel.trim()}
                className="w-full bg-slate-900 hover:bg-slate-800"
                data-testid="add-utility-type-btn"
              ><Plus className="w-4 h-4 mr-2" />Aggiungi tipo</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ClientPreviewModal
        clientId={previewClientId}
        open={!!previewClientId}
        onClose={() => setPreviewClientId(null)}
        assetTypes={['utenze']}
      />
    </div>
  );
}
